import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  InlineAlert,
  Link,
  Pane,
  SegmentedControl,
  Text,
  TextInput,
  toaster,
} from "evergreen-ui";
import useControlHub from "../hooks/useControlHub";

const OPTIONS = {
  payNow: "PAY_NOW",
  payByDueDate: "PAY_BY_DUE_DATE",
  payScheduled: "PAY_SCHEDULED",
  payExternal: "PAY_EXTERNAL",
};

const InvoicesPayDialog = ({ invoices, onClose, onFailure, onSuccess }) => {
  const { api, company, currency } = useControlHub();
  const [date, setDate] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [option, setOption] = useState(OPTIONS.payExternal);
  const [message, setMessage] = useState(null);

  const handleScheduledDateChange = useCallback(({ target: { value } }) => {
    setScheduledDate(value);
  }, []);

  useEffect(() => {
    if (option) {
      switch (option) {
        case OPTIONS.payNow:
          setDate("now");
          setMessage(<Text>Bills will get paid as soon as possible</Text>);
          break;
        case OPTIONS.payByDueDate:
          setDate("due_date");
          setMessage(
            <Text>Bills will get paid on their respective due dates</Text>
          );
          break;
        case OPTIONS.payScheduled:
          setDate(scheduledDate);
          setMessage(
            <Text>
              Bills will get paid on
              <br />
              <TextInput
                onChange={handleScheduledDateChange}
                type="date"
                value={scheduledDate}
              />
            </Text>
          );
          break;
        case OPTIONS.payExternal:
          setDate("external");
          setMessage(
            <Text>
              Pay Bills outside of ControlHub and mark them as paid for record
              keeping
            </Text>
          );
          break;
        default:
          setMessage();
          break;
      }
    }
  }, [handleScheduledDateChange, option, scheduledDate]);

  const handleOptionChange = useCallback((value) => {
    setOption(value);
  }, []);

  const handlePayConfirm = useCallback(() => {
    setLoading(true);
    Promise.all(
      invoices.map((invoice) =>
        api.setBillPayment({ billID: invoice.id, date })
      )
    )
      .then((results) => {
        const unsuccessful = results.filter((result) => !result.success);
        if (unsuccessful.length) {
          unsuccessful
            .filter((data) => data.message)
            .forEach((data) => toaster.danger(data.message));
          throw new Error("Some payment scheduling was unsuccessful");
        }
        toaster.success("Bill(s) scheduled for payment successfully");
        onSuccess();
      })
      .catch(() => {
        setLoading(false);
        onFailure(
          new Error(
            "Error: Bill(s) failed to get scheduled for payment. " +
              "Please contact support@controlhub.com"
          )
        );
      });
  }, [api, date, invoices, onFailure, onSuccess]);

  if (!invoices) {
    return;
  }

  return (
    <Dialog
      confirmLabel={
        option === OPTIONS.payExternal ? "Pay Externally" : "Confirm Payments"
      }
      isConfirmDisabled={
        option !== OPTIONS.payExternal && !company.features.billPayments
      }
      isConfirmLoading={loading}
      isShown={true}
      onCloseComplete={onClose}
      onConfirm={handlePayConfirm}
      title="Confirm Bill(s) Payment"
    >
      <Pane
        alignItems="center"
        display="flex"
        flexDirection="column"
        margin={16}
      >
        <Pane display="flex" justifyContent="space-between" width="100%">
          <Text>{`${invoices.length} Bill(s) selected`}</Text>
          <Text>{`Total: ${invoices
            .reduce(
              (total, invoice) => total.add(currency(invoice.amount)),
              currency(0)
            )
            .format()}`}</Text>
        </Pane>
        <SegmentedControl
          marginY={16}
          onChange={handleOptionChange}
          options={[
            { label: "Pay Now", value: OPTIONS.payNow },
            { label: "Pay by Due Date", value: OPTIONS.payByDueDate },
            { label: "Schedule Payment", value: OPTIONS.payScheduled },
            { label: "Pay Externally", value: OPTIONS.payExternal },
          ]}
          value={option}
          width="100%"
        />
        <Pane
          display="flex"
          flexDirection="column"
          justifyContent="center"
          marginY={16}
          minHeight="5rem"
        >
          {message}
        </Pane>
        {!company.features.billPayments && option !== OPTIONS.payExternal ? (
          <InlineAlert>
            Please contact{" "}
            <Link href="mailto:support@controlhub.com">
              support@controlhub.com
            </Link>{" "}
            to enable payments
          </InlineAlert>
        ) : null}
      </Pane>
    </Dialog>
  );
};

export default InvoicesPayDialog;
